import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import api, { fileToDataUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImagePlus, Download, RefreshCw, Film, X, Music } from "lucide-react";
import { ShareBar } from "@/components/ShareBar";

const BADGES = ["Just Listed", "Open House", "New Price", "Coming Soon", "Sold"];
const ORIENTATIONS = [
  { id: "reel", label: "Reel · 9:16 (IG/TikTok)" },
  { id: "square", label: "Square · 1:1" },
  { id: "wide", label: "Wide · 16:9 (YouTube)" },
];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoView({ prefill }) {
  const [slides, setSlides] = useState([]); // {dataUrl, caption}
  const [suggested, setSuggested] = useState([]);
  const [logo, setLogo] = useState("");
  const [headshot, setHeadshot] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    badge: "Just Listed", brand_color: "#D4AF37", agent_name: "", agent_phone: "",
    orientation: "reel", seconds_per_slide: 3, music: true,
  });
  const photoRef = useRef(null);
  const logoRef = useRef(null);
  const headshotRef = useRef(null);

  useEffect(() => {
    if (!prefill?.ts) return;
    setSuggested(prefill.captions || []);
    setF((prev) => ({
      ...prev,
      badge: prefill.badge || prev.badge,
      agent_name: prefill.agent_name || prev.agent_name,
    }));
    setSlides((cur) => cur.map((s, i) => ({ ...s, caption: s.caption || (prefill.captions || [])[i] || "" })));
    toast.info("Captions pre-filled from your listing — just add photos.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.ts]);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const start = slides.length;
    const loaded = [];
    let j = 0;
    for (const file of files.slice(0, 8 - slides.length)) {
      const dataUrl = await fileToDataUrl(file);
      loaded.push({ dataUrl, caption: suggested[start + j] || "" });
      j += 1;
    }
    setSlides((s) => [...s, ...loaded].slice(0, 8));
    e.target.value = "";
  };

  const removeSlide = (i) => setSlides((s) => s.filter((_, idx) => idx !== i));
  const setCaption = (i, v) => setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, caption: v } : sl)));

  const pick = (setter) => async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setter(await fileToDataUrl(file));
    e.target.value = "";
  };

  const generate = async () => {
    if (!slides.length) { toast.error("Add at least one listing photo."); return; }
    setLoading(true); setResult("");
    try {
      const { data } = await api.post("/video", {
        photos_base64: slides.map((s) => s.dataUrl),
        captions: slides.map((s) => s.caption),
        badge: f.badge, brand_color: f.brand_color,
        agent_name: f.agent_name, agent_phone: f.agent_phone,
        logo_base64: logo, headshot_base64: headshot,
        orientation: f.orientation, seconds_per_slide: Number(f.seconds_per_slide),
        music: f.music,
      });
      setResult(`${BACKEND_URL}${data.url}`);
      toast.success("Your listing video is ready!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="video-panel">
        <div>
          <h2 className="font-semibold text-lg">Listing Video Maker</h2>
          <p className="text-sm text-slate-500 mt-1">Turn property photos into a branded, music-backed reel ready to post.</p>
        </div>

        <div>
          <Label>Listing photos <span className="text-slate-400">({slides.length}/8)</span></Label>
          {suggested.length > 0 && (
            <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2.5 py-1.5 mt-1.5" data-testid="video-prefill-hint">
              {suggested.length} captions ready from your listing — they'll attach to photos as you add them.
            </p>
          )}
          <div className="mt-1.5 grid grid-cols-3 gap-2">
            {slides.map((s, i) => (
              <div key={i} className="relative group">
                <img src={s.dataUrl} alt="" className="w-full h-20 object-cover rounded-md" />
                <button onClick={() => removeSlide(i)} data-testid={`video-remove-${i}`}
                  className="absolute -top-1.5 -right-1.5 bg-slate-900 text-white rounded-full p-0.5 opacity-90 hover:opacity-100">
                  <X className="h-3 w-3" />
                </button>
                <Input value={s.caption} onChange={(e) => setCaption(i, e.target.value)}
                  data-testid={`video-caption-${i}`} placeholder="Caption…"
                  className="mt-1 h-7 text-xs px-2" />
              </div>
            ))}
            {slides.length < 8 && (
              <div onClick={() => photoRef.current?.click()} data-testid="video-add-photos"
                className="h-20 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center cursor-pointer hover:border-slate-400 text-slate-400">
                <ImagePlus className="h-5 w-5" />
              </div>
            )}
          </div>
          <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Badge</Label>
            <Select value={f.badge} onValueChange={(v) => setF({ ...f, badge: v })}>
              <SelectTrigger className="mt-1.5" data-testid="video-badge"><SelectValue /></SelectTrigger>
              <SelectContent>{BADGES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Brand color</Label>
            <input type="color" value={f.brand_color} onChange={(e) => setF({ ...f, brand_color: e.target.value })} className="mt-1.5 h-10 w-full rounded border border-slate-200" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Format</Label>
            <Select value={f.orientation} onValueChange={(v) => setF({ ...f, orientation: v })}>
              <SelectTrigger className="mt-1.5" data-testid="video-orientation"><SelectValue /></SelectTrigger>
              <SelectContent>{ORIENTATIONS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Seconds / photo</Label>
            <Select value={String(f.seconds_per_slide)} onValueChange={(v) => setF({ ...f, seconds_per_slide: Number(v) })}>
              <SelectTrigger className="mt-1.5" data-testid="video-duration"><SelectValue /></SelectTrigger>
              <SelectContent>{[2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}s</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div><Label>Agent name</Label><Input value={f.agent_name} onChange={(e) => setF({ ...f, agent_name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Agent phone</Label><Input value={f.agent_phone} onChange={(e) => setF({ ...f, agent_phone: e.target.value })} className="mt-1.5" /></div>
        </div>

        <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
          <span className="flex items-center gap-2 text-sm text-slate-700"><Music className="h-4 w-4 text-slate-500" /> Background music</span>
          <Switch checked={f.music} onCheckedChange={(v) => setF({ ...f, music: v })} data-testid="video-music-toggle" />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()}>{logo ? "Logo ✓" : "Upload logo"}</Button>
          <Button variant="outline" size="sm" onClick={() => headshotRef.current?.click()}>{headshot ? "Headshot ✓" : "Upload headshot"}</Button>
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={pick(setLogo)} />
          <input ref={headshotRef} type="file" accept="image/*" className="hidden" onChange={pick(setHeadshot)} />
        </div>

        <Button onClick={generate} disabled={loading} data-testid="video-generate-btn" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
          {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Rendering video…</> : <><Film className="h-4 w-4 mr-2" /> Create video</>}
        </Button>
        {loading && <p className="text-xs text-center text-slate-400">This can take up to a minute.</p>}
      </div>

      <div>
        {!result ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg h-full min-h-[300px] flex items-center justify-center text-slate-400 text-center px-6" data-testid="video-empty">
            Your branded listing video preview appears here.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <video src={result} controls playsInline className="w-full rounded-lg bg-black" data-testid="video-result" />
            <a href={result} download="listing-video.mp4">
              <Button data-testid="video-download-btn" className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white"><Download className="h-4 w-4 mr-2" /> Download MP4</Button>
            </a>
            <ShareBar url={result} caption={slides.find((s) => s.caption)?.caption || f.agent_name || "Check out this listing!"} downloadName="listing-video.mp4" testidPrefix="video-share" />
          </div>
        )}
      </div>
    </div>
  );
}
